// src/telas/editar-tecnico/index.tsx

import styles from "./styles.module.css";
import { useNavigate, useParams } from "react-router-dom";
import {
  Dispatch,
  SetStateAction,
  createContext,
  useContext,
  useEffect,
  useState,
} from "react";
import { RxDotFilled } from "react-icons/rx";
import { DadosBasicos } from "./dados-basicos";
import { Endereco } from "./endereco";
import { Contato } from "./contato";
import { CadastroType, TecnicoDefault } from "../../context/parceiro-cadastro";
import { AuthFetchJson, url } from "../../api";
import Swal from "sweetalert2";
import { ContextRegister } from "../../context";
import { Menu } from "../menu";
import { Utils } from "../../utils";
import { FaArrowLeft, FaArrowRight } from "react-icons/fa";

export const FormularioContext = createContext<{
  formulario: CadastroType;
  setFormulario: Dispatch<SetStateAction<CadastroType>>;
} | null>(null);

const TABS = [
  { key: "dadosbasicos", label: "Dados Básicos", step: 1 },
  { key: "endereco", label: "Endereço", step: 2 },
  { key: "contato", label: "Contato", step: 3 },
] as const;

type TabKey = (typeof TABS)[number]["key"];

export function EditarTecnico() {
  const {
    state: { usuario },
  } = useContext(ContextRegister);

  const parametros = useParams();
  const navigate = useNavigate();

  const [tab, setTab] = useState<TabKey>("dadosbasicos");
  const [formulario, setFormulario] = useState(TecnicoDefault);

  const currentStep = TABS.find((t) => t.key === tab)?.step ?? 1;

  /* ── Validações por etapa ───────────────────────────────── */
  const isDadosBasicosValido = () => {
    const { A2_CGC, A2_NOME } = formulario;
    if (formulario.A2_TIPO === "J") {
      return !!(A2_CGC && A2_NOME && formulario.A2_NREDUZ);
    }
    return !!(A2_CGC && A2_NOME);
  };

  const isEnderecoValido = () => {
    const { A2_CEP, A2_END, A2_BAIRRO, A2_EST, A2_MUN, A2_ZNUMERO } =
      formulario;
    return !!(A2_CEP && A2_END && A2_BAIRRO && A2_EST && A2_MUN && A2_ZNUMERO);
  };

  const isContatoValido = () => {
    const { A2_TEL, A2_EMAIL } = formulario;
    return !!(A2_TEL && A2_EMAIL);
  };

  const isCurrentStepValido = () => {
    if (tab === "dadosbasicos") return isDadosBasicosValido();
    if (tab === "endereco") return isEnderecoValido();
    if (tab === "contato") return isContatoValido();
    return false;
  };

  /* ── Renderização da aba ────────────────────────────────── */
  function selectedTab() {
    switch (tab) {
      case "dadosbasicos":
        return <DadosBasicos />;
      case "endereco":
        return <Endereco />;
      case "contato":
        return <Contato />;
      default:
        return <DadosBasicos />;
    }
  }

  /* ── Navegação e submit ─────────────────────────────────── */
  function handleBack() {
    if (tab === "endereco") setTab("dadosbasicos");
    if (tab === "contato") setTab("endereco");
  }

  function handleNext() {
    if (tab === "dadosbasicos") {
      setTab("endereco");
      return;
    }

    if (tab === "endereco") {
      setTab("contato");
      return;
    }

    // tab === "contato" → submete
    if (tab === "contato") {
      let aux = { ...formulario };

      aux.A2_DTNASC = aux.A2_DTNASC?.replaceAll("-", "");
      aux.A2_CEP = aux.A2_CEP.replace(/\D/g, "");
      aux.A2_CGC = aux.A2_CGC.replace(/\D/g, "");
      if (aux.A2_NREDUZ === "") {
        aux.A2_NREDUZ = aux.A2_NOME.substring(0, 20);
      }

      if (aux.A2_ZOBSERV) {
        aux.A2_ZOBSERV = aux.A2_ZOBSERV.substring(0, 40);
      }

      if (aux.A2_ZSENHA === "" || aux.A2_ZSENHA_CONFIRM !== aux.A2_ZSENHA) {
        delete aux.A2_ZSENHA;
      }

      delete aux.A2_ZSENHA_CONFIRM;

      Swal.fire({
        title: "Cadastro!",
        html: "Aguarde, salvando seus dados no sistema.",
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      AuthFetchJson(url + "ALTERATECNICO", {
        body: JSON.stringify({
          dados: { ...aux, PARCEIRO: usuario.A2_COD, LOJA: usuario.A2_LOJA },
        }),
        method: "POST",
      })
        .then((retorno: any) => {
          if (retorno.sucesso === "T") {
            Swal.fire({
              position: "top-end",
              icon: "success",
              title: "Alteração realizada com sucesso!",
              showConfirmButton: false,
              timer: 1500,
            });

            navigate("/tecnicos");
          } else {
            Swal.fire({
              position: "top-end",
              icon: "error",
              title: "Erro ao alterar técnico, " + retorno.mensagem,
              showConfirmButton: false,
              timer: 1500,
            });
          }
        })
        .catch((error) => {
          Swal.fire({
            position: "top-end",
            icon: "error",
            title: "Erro ao alterar técnico! " + error,
            showConfirmButton: false,
            timer: 1500,
          });
          console.log(error);
        })
        .finally(() => {
          if (Swal.isLoading()) {
            Swal.close();
          }
        });
    }
  }

  /* ── Carrega dados do técnico ───────────────────────────── */
  function getDadosTecnico() {
    Swal.fire({
      title: "Meus Dados",
      html: "Aguarde enquanto carregamos seus dados.",
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    const controller = new AbortController();
    const signal = controller.signal;

    AuthFetchJson(`${url}COLETAFORNECEDOR`, {
      signal,
      method: "POST",
      body: JSON.stringify({
        A2_COD: atob(parametros.A2_COD || ""),
        A2_LOJA: usuario.A2_LOJA,
        PARCEIRO: usuario.A2_COD,
        LOJA: usuario.A2_LOJA,
        TECFOR: "S",
      }),
    })
      .then((response) => {
        if (response.sucesso !== "T") {
          Swal.fire({
            title: "Meus Dados",
            html: "Houve um erro ao buscar seus dados." + response.mensagem,
          });
          return;
        }
console.log("Dados completos do técnico:", JSON.stringify(response.dados[0]));
        response.dados[0].A2_CGC = Utils.maskaraCnpj(response.dados[0].A2_CGC);
        setFormulario(response.dados[0]);
      })
      .catch(() => {
        Swal.fire({
          title: "Meus Dados",
          html: "Houve um erro ao buscar seus dados.",
        });
      })
      .finally(() => {
        if (Swal.isLoading()) {
          Swal.close();
        }
      });

    return () => {
      controller.abort();
    };
  }

  useEffect(() => {
    return getDadosTecnico();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isDisabled = !isCurrentStepValido();

  /* ── Render ─────────────────────────────────────────────── */
  return (
    <div className={styles.container}>
      <div className={styles.blobTop} aria-hidden="true" />
      <div className={styles.blobBottom} aria-hidden="true" />

      <Menu />

      <div className={styles.content}>
        {/* Progress stepper */}
        <div className={styles.stepper}>
          {TABS.map((t, i) => {
            const isPast = currentStep > t.step;

            const stepValido =
              t.key === "dadosbasicos"
                ? isDadosBasicosValido()
                : t.key === "endereco"
                  ? isEnderecoValido()
                  : isContatoValido();

            const showCheck = isPast && stepValido;
            const showError = isPast && !stepValido;

            return (
              <div key={t.key} className={styles.stepperItem}>
                <button
                  className={`
                    ${styles.stepCircle}
                    ${tab === t.key ? styles.stepActive : ""}
                    ${showCheck ? styles.stepDone : ""}
                    ${showError ? styles.stepError : ""}
                  `}
                >
                  {showCheck ? "✓" : showError ? "✗" : t.step}
                </button>
                <span
                  className={`${styles.stepLabel} ${tab === t.key ? styles.stepLabelActive : ""}`}
                >
                  {t.label}
                </span>
                {i < TABS.length - 1 && (
                  <div
                    className={`${styles.stepLine} ${showCheck ? styles.stepLineDone : ""}`}
                  />
                )}
              </div>
            );
          })}
        </div>

        {/* Card */}
        <div className={styles.cardForm}>
          <div className={styles.cardHeader}>
            <h1>Editar Técnico</h1>
            <div className={styles.cardHeaderRow}>
              <h2 className={styles.cardTitle}>
                {TABS.find((t) => t.key === tab)?.label}
              </h2>
              <span className={styles.requiredNote}>
                <RxDotFilled size={16} /> Campos Obrigatórios
              </span>
            </div>
          </div>

          <FormularioContext.Provider value={{ formulario, setFormulario }}>
            {selectedTab()}
          </FormularioContext.Provider>

          <hr className={styles.divider} />

          <div className={styles.actions}>
            {tab !== "dadosbasicos" && (
              <button className={styles.backSecBtn} onClick={handleBack}>
                <FaArrowLeft size={16} /> Voltar
              </button>
            )}
            <button
              disabled={isDisabled}
              className={`
                ${tab === "contato" ? styles.nextBtnConfirmar : styles.nextBtnProximo}
                ${isDisabled ? styles.nextBtnDisabled : ""}
              `}
              onClick={handleNext}
            >
              {tab === "contato" ? "Confirmar e Finalizar" : "Próximo"}{" "}
              <FaArrowRight size={16} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
